import React, { useEffect, useRef, useState } from "react";
import { X, ArrowUpRight, ArrowLeft, Share2, Check } from "lucide-react";
import ReactMarkdown from "react-markdown";
import CommunityCard from "./CommunityCard";
import EmbedBlock from "./EmbedBlock";
import SubscribeForm from "./SubscribeForm";
import { fetchPosts, resolveImageUrl, buildShareUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

const POSTS_CACHE_KEY = "blog_all_posts_cache_v1";
const readCache = () => {
  try {
    const raw = localStorage.getItem(POSTS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeCache = (data) => {
  try {
    localStorage.setItem(POSTS_CACHE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
};

const DEFAULT_THUMB =
  "https://images.unsplash.com/photo-1666618207644-4de0226a3f85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwd29ya3NwYWNlfGVufDB8fHx8MTc3NzM5MzA2N3ww&ixlib=rb-4.1.0&q=85";

// Build a fully-qualified shareable URL for a single post via the backend
// share endpoint (returns OG/Twitter meta so social crawlers get previews).
const buildShareLink = (slug) => buildShareUrl(slug);

export const BlogModal = ({ open, onClose, initialPost = null }) => {
  const { settings } = useSettings();
  const [active, setActive] = useState(initialPost);
  const [posts, setPosts] = useState(() => readCache() || []);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setActive(initialPost);
  }, [initialPost, open]);

  // Sync the URL with the currently active post — gives every post a
  // shareable link without needing a routing change.
  useEffect(() => {
    if (typeof window === "undefined" || !open) return;
    const params = new URLSearchParams(window.location.search);
    if (active?.slug) {
      params.set("post", active.slug);
      window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
    } else {
      params.delete("post");
      const qs = params.toString();
      window.history.replaceState(
        {},
        "",
        qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
      );
    }
  }, [active, open]);

  // When modal closes, strip ?post= from the URL.
  // IMPORTANT: only strip AFTER the modal has been opened at least once —
  // otherwise a shareable link like /?post=slug gets wiped on initial mount
  // before Home.jsx's deep-link effect can read it. This pattern is immune
  // to React.StrictMode's double-effect-invocation because `open` is false
  // on both invocations, so hasOpenedRef stays false until a real open.
  const hasOpenedRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (open) {
      hasOpenedRef.current = true;
      return;
    }
    if (!hasOpenedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.has("post")) {
      params.delete("post");
      const qs = params.toString();
      window.history.replaceState(
        {},
        "",
        qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
      );
    }
  }, [open]);

  const handleShare = async () => {
    if (!active?.slug) return;
    const url = buildShareLink(active.slug);
    const shareData = {
      title: active.title,
      text: active.snippet || active.title,
      url,
    };
    // Use native share sheet on mobile when available
    if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
      try {
        await navigator.share(shareData);
        return;
      } catch (e) {
        // user cancelled — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Older browsers — last-resort prompt
      window.prompt("Copy this link:", url);
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading((prev) => (posts.length === 0 ? true : prev));
    fetchPosts()
      .then((data) => {
        if (!cancelled) {
          setPosts(data);
          writeCache(data);
        }
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // We intentionally only re-run when the modal opens; `posts.length` is
    // read to decide the initial spinner state and should not retrigger the
    // fetch when posts update from the API response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        if (active) setActive(null);
        else onClose();
      }
    };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, active, onClose]);

  // Client-side <title> + meta description updates when a post is opened.
  // Server-rendered OG tags still come from /api/share/:slug for social crawlers;
  // this handles the browser tab + Google's live crawl.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!open || !active) return;
    const prevTitle = document.title;
    const brand = settings.brand_name || "";
    const t =
      (active.meta_title || "").trim() ||
      (active.og_title || "").trim() ||
      active.title;
    document.title = brand ? `${t} — ${brand}` : t;

    const desc =
      (active.meta_description || "").trim() ||
      (active.og_description || "").trim() ||
      (active.snippet || "").trim();
    let metaEl = document.querySelector("meta[name='description']");
    const prevDesc = metaEl ? metaEl.getAttribute("content") : null;
    if (desc) {
      if (!metaEl) {
        metaEl = document.createElement("meta");
        metaEl.setAttribute("name", "description");
        document.head.appendChild(metaEl);
      }
      metaEl.setAttribute("content", desc);
    }

    return () => {
      document.title = prevTitle;
      if (metaEl && prevDesc !== null) metaEl.setAttribute("content", prevDesc);
    };
  }, [open, active, settings.brand_name]);

  if (!open) return null;

  return (
    <div
      data-testid="blog-modal"
      className="fixed top-0 left-0 right-0 bottom-[52px] z-50 backdrop-blur-2xl bg-white/85"
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 md:px-12 py-5 border-b border-black/10">
        <div className="flex items-center gap-4">
          {active && (
            <button
              data-testid="modal-back-btn"
              onClick={() => setActive(null)}
              className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.18em] hover:text-[#002FA7] transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={2} />
              Index
            </button>
          )}
          <span className="font-display text-xs font-bold uppercase tracking-[0.22em] text-zinc-400">
            {active ? `Post / ${active.tag}` : "All Blog Posts"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {active && (
            <button
              data-testid="share-post-btn"
              onClick={handleShare}
              className={`flex items-center gap-2 px-3 py-1.5 border font-display text-[10px] font-bold uppercase tracking-[0.18em] transition-colors ${
                copied
                  ? "border-emerald-600 text-emerald-600"
                  : "border-black hover:bg-black hover:text-white"
              }`}
              aria-label="Share this post"
              title="Copy shareable link"
            >
              {copied ? (
                <>
                  <Check size={12} strokeWidth={2.5} /> Link copied
                </>
              ) : (
                <>
                  <Share2 size={12} strokeWidth={2.5} /> Share
                </>
              )}
            </button>
          )}
          <button
            data-testid="modal-close-btn"
            onClick={onClose}
            className="group flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.18em] hover:text-[#FF2A00] transition-colors"
            aria-label="Close blog posts"
          >
            Close
            <span className="grid place-items-center w-7 h-7 border border-black group-hover:border-[#FF2A00] group-hover:bg-[#FF2A00] group-hover:text-white transition-colors">
              <X size={14} strokeWidth={2.5} />
            </span>
          </button>
        </div>
      </div>

      {/* Side rails — other posts (visible only when reading a single post) */}
      {active && (
        <SidePostsRails
          posts={posts.filter((p) => p.id !== active.id)}
          onSelect={(p) => {
            setActive(p);
            const sc = document.querySelector("[data-testid='blog-modal'] .modal-scroll");
            if (sc) sc.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}

      {/* Content */}
      <div className="modal-scroll h-[calc(100vh-52px-72px)] px-6 md:px-12 py-10">
        {!active ? (
          <div className="max-w-6xl mx-auto">
            <h2 className="font-display font-black text-5xl md:text-7xl tracking-[-0.03em] leading-[0.95] mb-2">
              {settings.index_heading || "The Index."}
            </h2>
            <p className="text-zinc-500 text-sm md:text-base max-w-xl mb-12">
              {settings.index_subtitle ||
                "Blog posts on venture, growth, and the operator mindset."}
            </p>

            {loading ? (
              <p className="font-display text-xs uppercase tracking-[0.2em] text-zinc-400">
                Loading…
              </p>
            ) : posts.length === 0 ? (
              <div className="border border-dashed border-black/30 p-12 text-center">
                <p className="font-display text-sm uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  {settings.index_empty_title || "No blog posts yet"}
                </p>
                <p className="text-zinc-400 text-sm">
                  {settings.index_empty_body ||
                    "The first post is being written. Subscribe to be notified when it lands."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
                {posts.map((post, i) => (
                  <a
                    key={post.id}
                    href={postHref(post.slug)}
                    onClick={linkClickHandler(setActive, post)}
                    data-testid={`modal-post-${post.slug}`}
                    className="group bg-white text-left flex flex-col hover:bg-[#0A0A0A] hover:text-white transition-colors duration-300 no-underline text-inherit"
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-[16/10] w-full overflow-hidden bg-zinc-100 border-b border-black/10">
                      <img
                        src={resolveImageUrl(post.thumb) || DEFAULT_THUMB}
                        alt=""
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                        draggable={false}
                      />
                      <span className="absolute top-3 left-3 font-display text-[9px] font-bold uppercase tracking-[0.22em] text-white px-2 py-0.5 bg-black/80 backdrop-blur-sm">
                        {post.tag}
                      </span>
                    </div>

                    {/* Body */}
                    <div className="p-6 md:p-7 flex-1 flex flex-col">
                      <div className="flex items-start justify-between mb-4">
                        <span className="font-display text-[10px] font-bold tracking-[0.2em] text-zinc-400">
                          {String(i + 1).padStart(2, "0")} /{" "}
                          {String(posts.length).padStart(2, "0")}
                        </span>
                      </div>
                      <h3 className="font-display font-bold text-lg md:text-xl tracking-tight leading-tight mb-3">
                        {post.title}
                      </h3>
                      <p className="text-sm text-zinc-500 group-hover:text-zinc-300 mb-6 line-clamp-3">
                        {post.snippet}
                      </p>
                      <div className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.18em] mt-auto">
                        Read post
                        <ArrowUpRight
                          size={14}
                          strokeWidth={2.5}
                          className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                        />
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        ) : (
          <article className="max-w-3xl mx-auto fade-up">
            {/* Tag + display date row */}
            <div className="flex items-baseline justify-between gap-4 mb-3">
              <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-[#002FA7]">
                {active.tag}
              </span>
              <span
                data-testid="post-display-date-rendered"
                className="font-display text-[10px] md:text-xs font-medium uppercase tracking-[0.22em] text-zinc-500"
              >
                {formatPostDate(active)}
              </span>
            </div>

            <h1 className="font-display font-black text-4xl md:text-6xl tracking-[-0.03em] leading-[0.95] mt-2 mb-6">
              {active.title}
            </h1>
            {active.snippet && (
              <p className="text-lg md:text-xl text-zinc-700 leading-relaxed mb-10 border-l-2 border-[#002FA7] pl-5">
                {active.snippet}
              </p>
            )}

            {(active.thumb || DEFAULT_THUMB) && (
              <figure className="mb-10">
                <div className="aspect-[16/9] w-full overflow-hidden border border-black/10">
                  <img
                    src={resolveImageUrl(active.thumb) || DEFAULT_THUMB}
                    alt={active.cover_caption || ""}
                    className="h-full w-full object-cover"
                  />
                </div>
                {active.cover_caption && (
                  <figcaption
                    data-testid="post-cover-caption"
                    className="mt-3 text-xs md:text-sm italic text-zinc-500 leading-relaxed"
                  >
                    {active.cover_caption}
                  </figcaption>
                )}
              </figure>
            )}

            {active.body && active.body.trim() ? (
              <BodyWithEmbeds
                body={active.body}
                embeds={active.embeds || []}
              />
            ) : (
              <div>
                <p className="text-sm uppercase tracking-[0.2em] font-display font-bold text-zinc-400 mb-4">
                  Coming soon
                </p>
                <p className="text-zinc-600 leading-relaxed">
                  This blog post is being written. Check back shortly — or
                  subscribe on the homepage to get it the moment it lands.
                </p>
                {/* Empty body — still render embeds at the bottom */}
                {Array.isArray(active.embeds) && active.embeds.length > 0 && (
                  <div data-testid="post-embeds" className="mt-6">
                    {active.embeds.map((em, i) => (
                      <EmbedBlock key={i} embed={em} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Lead-magnet capture — only when the post has a magnet configured */}
            {(active.lead_magnet_url || "").trim() && (
              <div
                data-testid="post-lead-magnet"
                className="mt-14 border-t border-black/10 pt-8"
              >
                <p className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-[#002FA7] mb-2">
                  Free with this post
                </p>
                <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-tight mb-3">
                  {active.lead_magnet_label || "Grab the download"}
                </h3>
                <p className="text-sm text-zinc-500 mb-5 max-w-lg">
                  Drop your email — we'll send the link to your inbox right away.
                </p>
                <SubscribeForm sourceSlug={active.slug} />
              </div>
            )}

            {/* Author block — bottom of every post (uses per-post overrides if set) */}
            <AuthorBlock settings={settings} post={active} />

            {/* Newsletter promo — very bottom, centered, no box */}
            <CommunityCard />
          </article>
        )}
      </div>
    </div>
  );
};

export default BlogModal;

// Format the post's display date — prefers the editable display_date,
// falls back to a nicely formatted created_at.
const formatPostDate = (post) => {
  if (post?.display_date && post.display_date.trim()) return post.display_date;
  if (!post?.created_at) return "";
  try {
    return new Date(post.created_at).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const AuthorBlock = ({ settings, post }) => {
  // Per-post overrides win; fall back to site-wide defaults.
  const name =
    (post?.author_name_override || "").trim() ||
    settings?.author_name ||
    "Author";
  const photo = resolveImageUrl(
    (post?.author_image_override || "").trim() || settings?.author_image || "",
  );
  const bio =
    (post?.author_bio_override || "").trim() || settings?.author_bio || "";
  const linkedin =
    (post?.author_linkedin_override || "").trim() ||
    settings?.author_linkedin ||
    "";
  const initial = (name || "A").trim().charAt(0).toUpperCase();

  const NameTag = linkedin ? "a" : "span";
  const nameProps = linkedin
    ? {
        href: linkedin,
        target: "_blank",
        rel: "noreferrer",
        className:
          "font-display font-bold text-base text-zinc-900 hover:text-[#002FA7] transition-colors underline decoration-zinc-300 hover:decoration-[#002FA7] underline-offset-4",
      }
    : { className: "font-display font-bold text-base text-zinc-900" };

  return (
    <div
      data-testid="post-author-block"
      className="mt-16 pt-8 border-t border-black/10 flex items-start justify-end gap-4 text-right"
    >
      <div className="flex-1 min-w-0 max-w-md">
        <p className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400 mb-0.5">
          {settings?.author_label || "Author"}
        </p>
        <NameTag data-testid="post-author-name" {...nameProps}>
          {name}
        </NameTag>
        {bio && (
          <p className="text-sm text-zinc-500 leading-relaxed mt-1 ml-auto">
            {bio}
          </p>
        )}
      </div>
      {photo ? (
        <img
          src={photo}
          alt={name}
          className="w-14 h-14 rounded-full object-cover border border-black/10 shrink-0"
        />
      ) : (
        <div className="w-14 h-14 rounded-full bg-[#0A0A0A] text-white grid place-items-center font-display font-bold text-lg shrink-0 border border-black/10">
          {initial}
        </div>
      )}
    </div>
  );
};

const DRIFT_CLASSES = ["tile-drift-a", "tile-drift-b", "tile-drift-c", "tile-drift-d", "tile-drift-e", "tile-drift-f"];

// Click handler that preserves native new-tab shortcuts:
// - plain left-click → SPA modal open (preventDefault)
// - cmd/ctrl-click, middle-click, right-click "Open in new tab" → browser default
const linkClickHandler = (fn, item) => (e) => {
  if (
    e.defaultPrevented ||
    e.button !== 0 ||
    e.metaKey ||
    e.ctrlKey ||
    e.shiftKey ||
    e.altKey
  ) {
    return;
  }
  e.preventDefault();
  fn(item);
};

const postHref = (slug) => `/?post=${encodeURIComponent(slug || "")}`;

// Renders the post body while replacing [[embed:N]] shortcodes with the
// matching EmbedBlock at that exact position. Any embeds whose shortcode
// isn't found in the body are appended at the end (backwards-compat with
// posts written before shortcodes existed).
const EMBED_SHORTCODE = /\[\[embed:(\d+)\]\]/g;
const BodyWithEmbeds = ({ body, embeds }) => {
  const proseClass =
    "prose prose-zinc max-w-none prose-headings:font-display prose-headings:font-bold prose-headings:tracking-tight prose-a:text-[#002FA7] prose-a:no-underline hover:prose-a:underline prose-img:border prose-img:border-black/10 prose-blockquote:border-[#002FA7]";
  const list = Array.isArray(embeds) ? embeds : [];

  if (list.length === 0) {
    return (
      <div
        className={proseClass}
        dangerouslySetInnerHTML={{ __html: body }}
      />
    );
  }

  // Split the HTML body by shortcodes, remembering which indexes were used.
  const chunks = [];
  const usedIndexes = new Set();
  let lastPos = 0;
  let match;
  const re = new RegExp(EMBED_SHORTCODE.source, "g");
  while ((match = re.exec(body)) !== null) {
    const idx = parseInt(match[1], 10);
    if (Number.isNaN(idx) || idx < 0 || idx >= list.length) continue;
    chunks.push({ kind: "html", html: body.slice(lastPos, match.index) });
    chunks.push({ kind: "embed", idx });
    usedIndexes.add(idx);
    lastPos = match.index + match[0].length;
  }
  chunks.push({ kind: "html", html: body.slice(lastPos) });

  const leftovers = list
    .map((em, i) => ({ em, i }))
    .filter(({ i }) => !usedIndexes.has(i));

  return (
    <>
      {chunks.map((c, i) =>
        c.kind === "html" ? (
          c.html ? (
            <div
              key={`h-${i}`}
              className={proseClass}
              dangerouslySetInnerHTML={{ __html: c.html }}
            />
          ) : null
        ) : (
          <EmbedBlock key={`e-${c.idx}`} embed={list[c.idx]} />
        ),
      )}
      {leftovers.length > 0 && (
        <div data-testid="post-embeds" className="mt-6">
          {leftovers.map(({ em, i }) => (
            <EmbedBlock key={`tail-${i}`} embed={em} />
          ))}
        </div>
      )}
    </>
  );
};

const SideTile = ({ post, onSelect, drift, number }) => (
  <a
    href={postHref(post.slug)}
    onClick={linkClickHandler(onSelect, post)}
    data-testid={`side-tile-${post.slug}`}
    className={`group ${drift} block bg-white border border-black/90 hover:border-[#002FA7] transition-colors duration-300 shadow-[5px_5px_0_0_#0A0A0A] hover:shadow-[5px_5px_0_0_#002FA7] text-left overflow-hidden no-underline text-inherit`}
    style={{
      width: "clamp(120px, 11vw, 168px)",
      height: "clamp(120px, 11vw, 168px)",
    }}
    aria-label={`Read ${post.title}`}
  >
    <div className="h-1/2 w-full overflow-hidden border-b border-black/90">
      <img
        src={resolveImageUrl(post.thumb) || DEFAULT_THUMB}
        alt=""
        className="h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
        draggable={false}
      />
    </div>
    <div className="h-1/2 w-full p-2 flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="font-display text-[8px] font-bold tracking-[0.18em] uppercase text-[#002FA7]">
            {post.tag || "Notes"}
          </span>
          <span className="font-display text-[8px] font-medium text-zinc-400">
            {String(number).padStart(2, "0")}
          </span>
        </div>
        <h4 className="font-display font-bold text-[10px] leading-[1.15] text-zinc-900 line-clamp-2">
          {post.title}
        </h4>
      </div>
    </div>
  </a>
);

/**
 * Floating side rails of related posts shown while reading a single post.
 * Hidden on smaller screens (lg+ only) to avoid colliding with the article column.
 * Sorted by recency (newest first), excluding the active post (already filtered).
 */
const SidePostsRails = ({ posts, onSelect }) => {
  if (!posts || posts.length === 0) return null;

  // Sorted newest first (created_at desc)
  const sorted = [...posts].sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || ""),
  );

  // Split: alternating left/right so left always gets older-rank odd, right gets even.
  // We cap at 3 per side to keep the rail clean.
  const left = sorted.filter((_, i) => i % 2 === 0).slice(0, 3);
  const right = sorted.filter((_, i) => i % 2 === 1).slice(0, 3);

  return (
    <>
      <aside
        data-testid="side-rail-left"
        aria-label="Related posts"
        className="hidden lg:flex absolute left-4 xl:left-8 top-[88px] bottom-4 flex-col justify-around items-start gap-3 pointer-events-none"
      >
        {left.map((p, i) => (
          <div key={p.id} className="pointer-events-auto">
            <SideTile
              post={p}
              onSelect={onSelect}
              drift={DRIFT_CLASSES[i % DRIFT_CLASSES.length]}
              number={i * 2 + 1}
            />
          </div>
        ))}
      </aside>
      <aside
        data-testid="side-rail-right"
        aria-label="Related posts"
        className="hidden lg:flex absolute right-4 xl:right-8 top-[88px] bottom-4 flex-col justify-around items-end gap-3 pointer-events-none"
      >
        {right.map((p, i) => (
          <div key={p.id} className="pointer-events-auto">
            <SideTile
              post={p}
              onSelect={onSelect}
              drift={DRIFT_CLASSES[(i + 3) % DRIFT_CLASSES.length]}
              number={i * 2 + 2}
            />
          </div>
        ))}
      </aside>
    </>
  );
};
